import { useEffect, useState } from 'react';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import dashLogo from '../../images/HUGHES-DIGITAL-SIGNAGE_BLUE.png';

interface LoginProps {
  actionUrl: string;
}

export function MssLogin({ actionUrl }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
  const signupUrl = document.querySelector<HTMLMetaElement>('meta[name="dash-signup-url"]')?.content ?? '#';

  const handleSubmit = () => {
    if (rememberMe) {
      document.cookie = `email=${encodeURIComponent(email)}; path=/; max-age=31536000`;
    } else {
      document.cookie = 'email=; path=/; max-age=0';
    }
    setIsLoading(true);
  };

  useEffect(() => {
    const match = document.cookie.match(/(?:^| )email=([^;]+)/);
    if (match?.[1]) {
      setEmail(decodeURIComponent(match[1]));
      setRememberMe(true);
    }
  }, []);

  return (
    <div className='min-h-screen bg-zinc-50 flex items-center justify-center p-8'>
      <div className='w-full max-w-md'>
        <div className='flex items-center justify-center mb-8'>
          <img src={dashLogo} alt='DashSignage' style={{ width: '500px' }} />
        </div>

        <div className='rounded-lg p-0'>
          <form onSubmit={handleSubmit} action={actionUrl} method="post" className='space-y-5'>
            <input type="hidden" name="authenticity_token" value={csrfToken} />

            <div className='space-y-1'>
              <Label htmlFor='email' className='text-sm font-normal text-black-950'>Email address</Label>
              <Input
                id='email'
                name='user[email]'
                type='email'
                placeholder='you@example.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className='h-11 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
              />
            </div>

            <div className='space-y-1'>
              <div className='flex items-center justify-between'>
                <Label htmlFor='password' className='text-sm font-normal text-black-950'>Password</Label>
                <a href='/users/password/new' className='text-sm text-blue-600 hover:text-blue-700 transition-colors'>
                  Forgot password?
                </a>
              </div>
              <div className='relative'>
                <Input
                  id='password'
                  name='user[password]'
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Enter your password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className='h-11 pr-10 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors'
                >
                  {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="remember_me"
                name="user[remember_me]"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300"
              />
              <Label htmlFor="remember_me" className="text-sm font-normal text-zinc-700">
                Remember me
              </Label>
            </div>
            <Button
              type='submit'
              className='w-full h-11 bg-blue-700 hover:bg-blue-800 transition-colors'
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className='w-4 h-4 mr-2' />
                  Sign in
                </>
              )}
            </Button>
          </form>
        </div>

        <div className='mt-6 text-center'>
          <p className='text-sm text-zinc-600'>
            Don't have an account?{' '}
            <a href={signupUrl} target='_blank' rel='noreferrer' className='text-blue-600 hover:text-blue-700 transition-colors'>
              Create an account
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default MssLogin;
